import type { ApiCommerceStandaloneOfferV2 } from "@bridge-applications/pb-types";
import { useTranslation } from "react-i18next";
import { BundleOfferDialog } from "../BundleOfferDialog/BundleOfferDialog";
import {
    standaloneBundleArtwork,
    standaloneCheckoutOrder,
    standaloneOfferPrice,
} from "../../../logic/helpers/standaloneOffers";
import { resolveLocalizedText } from "../../../logic/helpers/localizedText";
import { formatIntegerAmount } from "../../../logic/helpers/commerce";

interface StandaloneBundleOfferDialogProps {
    offer: ApiCommerceStandaloneOfferV2;
    clockOffsetMs: number;
    availability: "available" | "expired" | "unavailable";
    isBusy: boolean;
    error: string | null;
    onClose: () => void;
    onPurchase: () => void;
}

/** Validated catalog data is the only source of rewards, prices and artwork. */
export function StandaloneBundleOfferDialog({
    offer,
    clockOffsetMs,
    ...props
}: StandaloneBundleOfferDialogProps) {
    const { t, i18n } = useTranslation();
    const dialog = offer.presentation.dialog;
    if (dialog?.kind !== "BUNDLE") return null;
    const locale = i18n.resolvedLanguage ?? i18n.language;
    const prizes = offer.rewards.flatMap((reward) => {
        const artwork = dialog.prizes[reward.key];
        if (!artwork) return [];
        const amount = formatIntegerAmount(reward.amount, locale);
        const label =
            reward.kind === "SM_ENERGY"
                ? t("commerce.rewards.energy", { amount })
                : reward.kind === "WHEEL_SPINS"
                  ? t("commerce.bundle.holyWheelSpins", { amount })
                  : reward.kind === "CURRENCY"
                    ? t("commerce.rewards.currency", {
                          amount,
                          currency:
                              reward.currencyCode === "BEACH_COINS"
                                  ? t("gnomeChangingRoom.beachCoinsLabel")
                                  : reward.currencyCode,
                      })
                    : reward.itemCode === "shirt-016"
                      ? t("commerce.bundle.whiteFurCoat", { amount })
                      : reward.itemCode === "shirt-015"
                        ? t("commerce.bundle.purpleCaptainJacket", { amount })
                        : t("commerce.rewards.unknown");
        return [
            {
                key: reward.key,
                amount: reward.amount,
                label,
                artwork: { ...artwork, image: artwork.image.url },
            },
        ];
    });
    return (
        <BundleOfferDialog
            {...props}
            isOpen
            title={resolveLocalizedText(dialog.title, locale)}
            artwork={standaloneBundleArtwork(dialog.artwork)}
            prizes={prizes}
            price={standaloneOfferPrice(offer)}
            canResumeCheckout={standaloneCheckoutOrder(offer) !== null}
            expiresAt={
                dialog.showCountdown && offer.windowEndsAt
                    ? new Date(
                          Date.parse(offer.windowEndsAt) - clockOffsetMs,
                      ).toISOString()
                    : null
            }
            purchaseCounts={dialog.showAvailability ? offer : undefined}
        />
    );
}
